import React, { useState } from "react";
import { Network, Shuffle, ArrowLeftRight, GitBranch, Users } from "lucide-react";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { KpiTile, ChartCard, Segmented, Skeleton, ErrorState, PersonCell, EmptyState } from "../../analytics/primitives";
import { FlowMatrix, SegmentBar } from "../../analytics/charts";
import { SERIES } from "../../analytics/palette";

const NetworkTab = ({ filters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useAnalyticsSection("network", filters);
  const [matrixMode, setMatrixMode] = useState("team");
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) return <Skeleton height={400} />;
  const d = q.data;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Shuffle} label={t("network.cross_team_share")} value={pctFmt(d.team_scope.cross_share)} progress={d.team_scope.cross_share} tone="teal" sub={t("network.cross_team_sub", { count: fmt.number(d.team_scope.cross_team) })} hint={t("network.cross_team_hint")} />
        <KpiTile icon={GitBranch} label={t("network.peer_share")} value={pctFmt(d.direction.peer_share)} progress={d.direction.peer_share} tone="purple" sub={d.direction.teams_with_manager ? t("network.managers_known", { count: d.direction.teams_with_manager }) : t("network.no_managers")} hint={t("network.peer_hint")} />
        <KpiTile icon={ArrowLeftRight} label={t("network.reciprocity")} value={pctFmt(d.reciprocity.mutual_share)} progress={d.reciprocity.mutual_share} tone="pink" sub={t("network.reciprocity_sub", { mutual: fmt.number(d.reciprocity.mutual_pairs), pairs: fmt.number(d.reciprocity.pairs) })} hint={t("network.reciprocity_hint")} />
        <KpiTile icon={Users} label={t("network.avg_counterparts")} value={d.connectivity.avg_counterparts === null ? "—" : fmt.number(d.connectivity.avg_counterparts, { maximumFractionDigits: 1 })} tone="blue" sub={t("network.people_connected", { count: fmt.number(d.connectivity.people_connected) })} hint={t("network.counterparts_hint")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("network.scope_title")} subtitle={t("network.scope_subtitle")} icon={Shuffle}>
          <SegmentBar
            segments={[
              { label: t("network.intra_team"), value: d.team_scope.intra_team, color: SERIES[0] },
              { label: t("network.cross_team"), value: d.team_scope.cross_team, color: SERIES[2] },
              { label: t("network.no_team"), value: d.team_scope.no_team, color: "#cbd5e1" },
            ]}
          />
        </ChartCard>
        <ChartCard title={t("network.direction_title")} subtitle={t("network.direction_subtitle")} icon={GitBranch}>
          {d.direction.teams_with_manager ? (
            <SegmentBar
              segments={[
                { label: t("network.manager_to_report"), value: d.direction.manager_to_report, color: SERIES[1] },
                { label: t("network.report_to_manager"), value: d.direction.report_to_manager, color: SERIES[3] },
                { label: t("network.peer"), value: d.direction.peer, color: SERIES[0] },
              ]}
            />
          ) : (
            <EmptyState message={t("network.no_managers_long")} height={80} />
          )}
        </ChartCard>
      </div>

      <ChartCard
        title={t("network.matrix_title")}
        subtitle={t("network.matrix_subtitle")}
        icon={Network}
        actions={<Segmented ariaLabel={t("chart_mode")} value={matrixMode} onChange={setMatrixMode} options={[{ value: "team", label: t("network.by_team") }, { value: "department", label: t("network.by_department") }]} />}
      >
        <FlowMatrix matrix={matrixMode === "team" ? d.team_matrix : d.department_matrix} emptyMessage={t("network.no_flows")} />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("network.top_pairs_title")} subtitle={t("network.top_pairs_subtitle")} icon={ArrowLeftRight}>
          {d.top_pairs.length === 0 ? (
            <EmptyState height={160} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.top_pairs.map((p, i) => (
                <li key={`${p.sender?.id}-${p.recipient?.id}`} className="flex items-center gap-2 py-2">
                  <span className="w-5 text-xs text-slate-400 text-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <PersonCell user={p.sender} />
                  </div>
                  <span className="text-slate-400 text-xs flex-shrink-0" aria-label={t("network.recognised")}>
                    {p.mutual ? "⇄" : "→"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <PersonCell user={p.recipient} />
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-slate-800 w-8 text-right">{fmt.number(p.count)}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
        <ChartCard title={t("network.most_connected_title")} subtitle={t("network.most_connected_subtitle")} icon={Users}>
          {d.connectivity.most_connected.length === 0 ? (
            <EmptyState height={160} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.connectivity.most_connected.map((row, i) => (
                <li key={row.user?.id || i} className="flex items-center gap-3 py-2">
                  <span className="w-5 text-xs text-slate-400 text-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <PersonCell user={row.user} />
                  </div>
                  <div className="text-right text-[11px] text-slate-500 flex-shrink-0">
                    <span title={t("network.sent_to")}>→ {fmt.number(row.sent_to)}</span>
                    <span className="mx-1">·</span>
                    <span title={t("network.received_from")}>← {fmt.number(row.received_from)}</span>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-slate-800 w-8 text-right">{fmt.number(row.counterparts)}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default NetworkTab;
